import {useEffect} from 'react';
import ChatBot, {useStyles, Button, Flow, Plugin, Settings, Styles, Theme} from 'react-chatbotify';
import MarkdownRenderer from '@rcb-plugins/markdown-renderer';
import {useIntl} from 'react-intl';
import * as _ from 'lodash';
import ButtonSendChatHistory from '../ButtonSendChatHistory';
import {useIsDesktop} from '../hooks';
import thLogo from '../../assets/t-h-logo.jpg';
import {background, primaryGreen, primaryOrange, secondaryGreen, white} from '../../styles/colors';
import styles from './ChatPopup.module.scss';

/** ChatProps extends react-chatbotify component props */
export interface ChatPopupProps {
  id: string;
  flow: Flow;
  settings?: Settings | undefined;
  styles?: Styles | undefined;
  themes?: Theme | Theme[] | undefined;
  plugins?: Plugin[] | undefined;
}

const commonStyles = {
  tooltipStyle: {
    direction: 'rtl',
    backgroundColor: primaryGreen,
    borderColor: primaryGreen,
    color: white
  },
  notificationBadgeStyle: {
    backgroundColor: primaryOrange
  },
  chatWindowStyle: {
    backgroundColor: background,
    height: '75vh',
    width: '50vw',
    zIndex: 1000000,
  },
  headerStyle: {
    background: primaryGreen,
    color: white,
    padding: '10px',
  },
  bodyStyle: {
    direction: 'rtl',
    padding: '16px',
    boxSizing: 'border-box',
  },
  chatInputContainerStyle: {
    borderColor: primaryGreen,
    backgroundColor: background,
  },
  chatInputAreaStyle: {
    border: '1px solid',
    borderColor: secondaryGreen
  },
  chatInputAreaFocusedStyle: {
    backgroundColor: background,
    boxShadow: `${primaryGreen} 0px 0px 5px`
  },
  chatInputAreaDisabledStyle: {},
  userBubbleStyle: {
    backgroundColor: secondaryGreen,
  },
  botBubbleStyle: {
    direction: 'rtl',
    maxWidth: '100%',
    marginLeft: 0,
    marginRight: 0,
    backgroundColor: white,
    border: '1px solid',
    borderColor: primaryGreen,
    color: primaryGreen,
    textAlign: 'right',
  },
  botOptionStyle: {
    backgroundColor: primaryGreen,
    borderColor: primaryGreen,
    color: white
  },
  botOptionHoveredStyle: {
    backgroundColor: white,
    borderColor: primaryGreen,
    color: primaryGreen,
  },
  botCheckboxRowStyle: {
    width: '39%',
    backgroundColor: white,
    borderColor: primaryGreen,
    color: primaryGreen
  },
  botCheckboxNextStyle: {
    backgroundColor: white,
    borderColor: primaryGreen,
    color: primaryGreen,
    display: 'flex',
    alignItems: 'end',
    justifyContent: 'center',
  },
  botCheckMarkStyle: {
    marginLeft: 8,
    border: '1px solid',
    borderColor: primaryGreen,
    background: white,
  },
  botCheckMarkSelectedStyle: {
    marginLeft: 8,
    border: '1px solid',
    borderColor: secondaryGreen,
    background: secondaryGreen
  },
  characterLimitStyle: {},
  characterLimitReachedStyle: {},
  chatHistoryLineBreakStyle: {},
  chatMessagePromptStyle: {
    direction: 'rtl',
    textAlign: 'right',
    background: primaryGreen,
  },
  chatMessagePromptHoveredStyle: {},
  footerStyle: {
    minHeight: '40px',
    background: primaryGreen,
    color: white,
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  loadingSpinnerStyle: {
    borderTopColor: secondaryGreen
  },
  mediaDisplayContainerStyle: {},
  chatButtonStyle: {
    position: 'fixed',
    background: white,
    border: '5px solid',
    borderColor: primaryGreen
  },
  chatHistoryButtonStyle: {},
  chatHistoryButtonHoveredStyle: {
    color: secondaryGreen,
    borderColor: secondaryGreen,
  },
  sendButtonStyle: {
    backgroundColor: secondaryGreen,
  },
  sendButtonHoveredStyle: {
    backgroundColor: primaryGreen,
  },
  sendButtonDisabledStyle: {},
  audioButtonStyle: {},
  audioButtonDisabledStyle: {},
  closeChatButtonStyle: {},
  emojiButtonStyle: {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emojiButtonDisabledStyle: {
    display: 'inline-flex'
  },
  fileAttachmentButtonStyle: {},
  fileAttachmentButtonDisabledStyle: {},
  notificationButtonStyle: {},
  notificationButtonDisabledStyle: {},
  voiceButtonStyle: {},
  voiceButtonDisabledStyle: {},
  chatIconStyle: {},
  audioIconStyle: {},
  audioIconDisabledStyle: {},
  closeChatIconStyle: {
    fill: white
  },
  emojiIconStyle: {
    fill: white
  },
  emojiIconDisabledStyle: {},
  fileAttachmentIconStyle: {
    fill: white
  },
  fileAttachmentIconDisabledStyle: {},
  notificationIconStyle: {
    fill: primaryOrange
  },
  notificationIconDisabledStyle: {},
  voiceIconStyle: {
    fill: white
  },
  voiceIconDisabledStyle: {},
  sendIconStyle: {
    fill: white
  },
  sendIconDisabledStyle: {},
  sendIconHoveredStyle: {},
  rcbTypingIndicatorContainerStyle: {},
  rcbTypingIndicatorDotStyle: {},
  toastPromptContainerStyle: {},
  toastPromptStyle: {},
  toastPromptHoveredStyle: {}
};

const ltrStyles = {
  tooltipStyle: {
    direction: 'ltr',
  },
  bodyStyle: {
    direction: 'ltr',
  },
  botBubbleStyle: {
    direction: 'ltr',
    botBubbleStyle: 'left',
  },
  chatMessagePromptStyle: {
    direction: 'ltr',
    botBubbleStyle: 'left',
  },
}

const mobileStyles = {
  botCheckboxRowStyle: {
    width: '80%',
  },
  chatButtonStyle: {
    bottom: 40,
  },
  tooltipStyle: {
    bottom: 50,
  },
  footerStyle: {
    background: primaryGreen,
    color: white,
    fontSize: 16,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'space-between',
  },
};

const ShedonFace = () => <div className={styles.shedon}/>;

export const ChatPopup = (props: ChatPopupProps) => {
  const intl = useIntl();
  const tooltipText = intl.formatMessage({
    id: 'chat-button-tooltip',
    defaultMessage: 'Talk to me! \uD83D\uDE0A'
  });
  const placeholderText = intl.formatMessage({
    id: 'chat-input-placeholder',
    defaultMessage: 'Type your message...'
  });
  const {settings, styles, ...rest} = props;
  const isDesktop = useIsDesktop();
  const {updateStyles} = useStyles();

  useEffect(() => {
    const localStyles = intl.locale === 'en' ? ltrStyles : {};
    const platformStyles = isDesktop ? {} : mobileStyles;
    updateStyles(_.merge(commonStyles, localStyles, platformStyles, styles));
  }, [isDesktop, intl.locale, styles]);

  return (
    <ChatBot
      key={isDesktop ? 'travel-hotels-ai-assistant-chat-desktop': 'travel-hotels-ai-assistant-chat-mobile'}
      settings={{
        chatButton: {icon: ShedonFace},
        chatHistory: {storageKey: 'travel_hotels_chatbot_llm_conversation', disabled: true},
        chatInput: {enabledPlaceholderText: placeholderText},
        general: {
          fontFamily: 'kedem-sans, sans-serif',
        },
        header: {
          title: (
            <div style={{
              cursor: 'pointer',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              fontSize: 20,
              fontWeight: 'bold'
            }}>
              Travel Hotels
            </div>
          ),
          showAvatar: true,
          avatar: thLogo,
          buttons: [Button.CLOSE_CHAT_BUTTON]
        },
        footer: {
          text: '',
          buttons: [<ButtonSendChatHistory/>]
        },
        event: {
          rcbChangePath: true,
          rcbToggleChatWindow: true,
          rcbTextAreaChangeValue: true,
        },
        tooltip: {
          text: tooltipText
        },
        notification: {volume: 0},
        ...settings
      }}
      styles={_.merge(commonStyles, styles)}
      plugins={[MarkdownRenderer({autoConfig: true})]}
      {...rest}
    />
  );
};
